'use client';

import { TaxonomyType, defaultLanguage } from '@stereoagency/constants';
import {
  ArticleHero,
  BadgeCard,
  BadgeListEntriesGridSection,
  Container,
  DividerSection,
  EventFooter,
  PageContent,
  useAccentColors
} from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import flush from 'just-flush';
import { twMerge } from '@stereoagency/tailwind-config';
import { ReactNode, useContext } from 'react';
import { eventQuery } from './eventQuery';
import { eventAsProps, findInKeyArray, formatProfileName } from '@/utils';
import { SpecialPagesContext } from '@/components/context';
import { SimpleBlockContent } from '@/components/sanity/client';

type Props = {
  lang: string;
  event: (typeof eventQuery)[1];
  taxonomyCounts?: Record<string, number>;
  eventContent?: ReactNode;
};

export default function EventPage({
  lang,
  event,
  eventContent,
  taxonomyCounts
}: Props) {
  const { t } = useTranslation('event');
  const { pages } = useContext(SpecialPagesContext);
  const hasContent = !!eventContent;

  const accentStyle = useAccentColors([
    'colors-link-fg-base',
    '--colors-purple-700'
  ]);

  const eventProps = eventAsProps(event, lang, { t });
  const lecturerInformation = findInKeyArray(event.lecturerInformation ?? [], [
    lang,
    defaultLanguage.id
  ]);
  const categories =
    event.taxonomies?.filter(
      (tax) => tax.type === TaxonomyType.EVENT_CATEGORY
    ) ?? [];

  if (!eventProps || !eventProps.title) {
    return null;
  }

  return (
    <>
      <Container>
        <div
          style={accentStyle}
          className={twMerge(
            'grid gap-6 grid-cols-1 grid-flow-row auto-rows-max',
            'md:grid-cols-[18rem_1fr] md:grid-rows-[auto_1fr] md:gap-y-9 md:gap-x-10',
            'lg:grid-cols-[23rem_1fr] lg:gap-x-12'
          )}
        >
          <div className="md:col-start-1 md:row-span-2">
            <BadgeCard
              badge={eventProps.badge}
              action={eventProps.action}
              metadata={eventProps.metadata}
              metaActions={eventProps.metaActions}
              secondaryAction={eventProps.secondaryAction}
              variant="standalone"
              className="sticky top-12 left-0 md:max-w-[18rem]"
            />
          </div>

          <div className="order-first md:col-start-2 md:row-start-1">
            <ArticleHero
              alignment="left"
              accentColor="--colors-purple-700"
              title={eventProps.title}
              label={eventProps.label}
              actions={flush(
                categories.map((cat) => {
                  const label = cat.name
                    ? findInKeyArray(cat.name, [lang, defaultLanguage.id])
                    : null;

                  if (
                    !label?.value ||
                    !cat.slug?.current ||
                    !pages.event_listing_page?.href
                  ) {
                    return null;
                  }

                  const url = `${
                    pages.event_listing_page.href
                  }?filters=${encodeURIComponent(cat.slug.current)}`;
                  const taxCount = taxonomyCounts?.[cat.slug.current] ?? 0;

                  return {
                    variant: 'button/border' as const,
                    href: url,
                    label: label.value,
                    suffix: `(${taxCount})`
                  };
                })
              )}
            />
            {hasContent && <DividerSection className="mt-9 hidden md:block" />}
          </div>

          <PageContent className="my-0 md:my-0 md:col-start-2 md:row-start-2">
            {eventContent}

            <EventFooter
              showDivider={hasContent}
              profiles={flush([
                event.lecturer
                  ? {
                      label: t('event:lecturer'),
                      name: event.lecturer,
                      subtitle: lecturerInformation?.value ? (
                        <SimpleBlockContent value={lecturerInformation.value} />
                      ) : null
                    }
                  : null,
                event.contact?.name
                  ? {
                      label: t('event:contact'),
                      name: formatProfileName(event.contact)
                    }
                  : null
              ])}
            />
          </PageContent>
        </div>
      </Container>

      {!!event.upcomingEvents.length && (
        <BadgeListEntriesGridSection
          accentColor="--colors-purple-700"
          title={t('event:upcoming-events')}
          action={
            pages.event_listing_page
              ? {
                  href: pages.event_listing_page.href,
                  label: t('event:all-events')
                }
              : undefined
          }
          cards={flush(
            event.upcomingEvents.map((evt) => {
              const asProps = eventAsProps(evt, lang, { t });
              if (!asProps || !asProps.title) return null;
              return {
                ...asProps,
                title: asProps.title,
                variant: 'inverted' as const
              };
            })
          )}
        />
      )}
    </>
  );
}
